<script setup lang="ts">
  import { computed, type ModelRef, onMounted, type Ref, ref, watch } from 'vue'
  import CreateOrganizationModal from '@/components/organization/CreateOrganizationModal.vue'
  import type { Organization } from '@/types/organization'
  import { useOrganizationStore } from '@/store/organization.store.ts'
  import { storeToRefs } from 'pinia'
  import type { CreateOrganization } from '@/model/create-organization.model.ts'
  import { DropDown, type Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { useUserStore } from '@/store/user.store'

  const { carriers, required = false } = defineProps<{
    label?: string
    carriers?: Organization[]
    required?: boolean
    error?: string | undefined
  }>()

  const userStore = useUserStore()

  const organizationStore = useOrganizationStore()
  const { organizations } = storeToRefs(organizationStore)
  const selectedOrganization: ModelRef<Organization | Nullish> = defineModel<
    Organization | Nullish
  >({
    default: null,
  })
  const show: Ref<boolean> = ref<boolean>(false)

  onMounted(organizationStore.fetchOrganizations)

  watch(
    () => selectedOrganization.value,
    () => {
      window.dispatchEvent(
        new CustomEvent('select-organization', { detail: selectedOrganization.value })
      )
    }
  )

  defineExpose({ selectedOrganisation: selectedOrganization })

  function selectCreatedOrganization(organizationData: CreateOrganization) {
    selectedOrganization.value =
      filteredOrganizations.value.find(
        organization => organization.organisatieNummer === organizationData.organizationNumber
      ) ?? null
  }

  const filteredOrganizations = computed(() => {
    const excluded = new Set(carriers?.map(carrier => carrier.id) ?? [])
    const seen = new Set<number>()

    return organizations.value.filter(org => {
      if (excluded.has(org.id) || seen.has(org.id)) return false
      seen.add(org.id)
      return true
    })
  })
</script>

<template>
  <drop-down
    v-model="selectedOrganization"
    :data="filteredOrganizations"
    :allow-new="true"
    :enable-search="true"
    label="Organisatie"
    @add="show = true"
    :required="required"
    :error="error"
    :data-validation-error="!!error"
  >
    <template #empty>{{ label || 'Selecteer adres gegevens' }}</template>
    <template #item="{ item }">
      <span
        class="selection-option flex-container"
        :class="{
          'user-organization': item.id == userStore.currentUser?.userOrganisatie.id,
        }"
      >
        <span class="item-name">
          {{ item.organisatieNaam }}
        </span>
        <span class="item-name">
          {{ item.organisatieNummer }}
        </span>
        <span class="item-name">
          {{ item.straatnaam }} {{ item.huisNummer }} {{ item.toevoeging }}
          {{ item.postcode }}
        </span>
        <span class="item-name">
          {{ item.land }}
        </span></span
      >
    </template>
  </drop-down>
  <create-organization-modal v-model:show="show" @created="selectCreatedOrganization" />
</template>

<style scoped>
  .item-name {
    flex: 1 0 0;
    text-align: left;
  }

  .flex-container {
    display: flex;
    justify-content: space-between;
  }

  .user-organization {
    text-decoration: underline;
  }
</style>
